/**
 * **Inside / Outside Bars** — a pale column behind each, from the chart's own
 * bars. Owner, 2026-10-13: Bar Intelligence (every plan), strict comparisons,
 * inside `accent` (blue), outside `warn` (amber). `docs/plans/bar-highlights.md`.
 *
 * The server lists the alias as geometry and answers nothing; the classifying is
 * here because it is two comparisons per bar and the bars are already loaded.
 */

import type { ChartGroup } from "@qufolio/model";

export const INSIDE_OUTSIDE_LAYER = "insideOutside";
export const INSIDE_COLOR = "accent";
export const OUTSIDE_COLOR = "warn";

export type BarKind = "inside" | "outside";

/**
 * How far the box reaches past the candle's high and low, as a share of that
 * bar's own range — a small margin so the wick tips sit inside it (owner,
 * 2026-10-13: the box around the candle, not the pane's height).
 */
export const HUG_PAD = 0.05;

/** The boxes' opacity until the user sets one — the forming band's ~13%. */
export const DEFAULT_OPACITY = 0.13;

/**
 * Whether an alias is a bar-highlight indicator — the editor offers it two
 * colours and an opacity, and no scale, smoothing, width or dash.
 */
export function isBarHighlightAlias(alias: string | undefined): boolean {
  return alias !== undefined && parseInsideOutside(alias) !== undefined;
}

/**
 * The plot's own choices: inside in `palette.upColor`, outside in
 * `palette.downColor`, opacity in `style.opacity` — the fields a paired plot
 * already stores, so nothing new reaches the contract.
 */
export interface InsideOutsideSettings {
  inside: boolean;
  outside: boolean;
  insideColor: string;
  outsideColor: string;
  opacity: number;
}

/** `insideOutside_<inside on|off>_<outside on|off>`; a bare name is both on. */
export function parseInsideOutside(alias: string): { inside: boolean; outside: boolean } | undefined {
  const [name, inside, outside] = alias.split("_");
  if (name !== "insideOutside") return undefined;
  return { inside: inside !== "off", outside: outside !== "off" };
}

/** The first Inside / Outside plot on the chart's price pane, parsed. */
export function insideOutsideOf(group: ChartGroup): InsideOutsideSettings | undefined {
  for (const pane of group.panes) {
    if (pane.role !== "price") continue;
    for (const plot of pane.plots ?? []) {
      if (plot.type !== "geometry" || !plot.outputAlias || plot.hidden) continue;
      const parsed = parseInsideOutside(plot.outputAlias);
      if (parsed) {
        return {
          ...parsed,
          insideColor: plot.palette?.upColor ?? INSIDE_COLOR,
          outsideColor: plot.palette?.downColor ?? OUTSIDE_COLOR,
          opacity: plot.style?.opacity ?? DEFAULT_OPACITY,
        };
      }
    }
  }
  return undefined;
}

/**
 * Each settled bar against the one before: **inside** when its high is below
 * and its low above, **outside** when its high is above and its low below —
 * strictly, so an equal high or low is neither.
 *
 * `settled` is how many leading bars are final; a forming last bar is left out
 * because its range still moves.
 */
export function classifyBars(
  bars: { t: readonly number[]; h: readonly number[]; l: readonly number[] },
  settled: number,
): { t: number; kind: BarKind; high: number; low: number }[] {
  const out: { t: number; kind: BarKind; high: number; low: number }[] = [];
  const n = Math.min(settled, bars.t.length, bars.h.length, bars.l.length);
  for (let i = 1; i < n; i++) {
    const h = bars.h[i]!;
    const l = bars.l[i]!;
    const ph = bars.h[i - 1]!;
    const pl = bars.l[i - 1]!;
    if (![h, l, ph, pl].every(Number.isFinite)) continue;
    if (h < ph && l > pl) out.push({ t: bars.t[i]!, kind: "inside", high: h, low: l });
    else if (h > ph && l < pl) out.push({ t: bars.t[i]!, kind: "outside", high: h, low: l });
  }
  return out;
}

/** What the chart paints: the kinds switched on, in their colours, around each candle. */
export function insideOutsideHighlights(
  group: ChartGroup,
  bars: { t: readonly number[]; h: readonly number[]; l: readonly number[] },
  settled: number,
): { t: number; color: string; low: number; high: number; opacity: number }[] {
  const on = insideOutsideOf(group);
  if (!on) return [];
  return classifyBars(bars, settled)
    .filter((b) => (b.kind === "inside" ? on.inside : on.outside))
    .map((b) => {
      const pad = (b.high - b.low) * HUG_PAD;
      return {
        t: b.t,
        color: b.kind === "inside" ? on.insideColor : on.outsideColor,
        low: b.low - pad,
        high: b.high + pad,
        opacity: on.opacity,
      };
    });
}

/** The legend's word for a kind (owner, 2026-10-13: the word alone, no dates or levels). */
export function kindLabel(kind: BarKind): string {
  return kind === "inside" ? "Inside" : "Outside";
}

/**
 * Each highlighted bar's kind by its time, for the value legend to read at the
 * bar under the pointer. Only the kinds switched on, so the legend never names
 * a bar the chart does not paint.
 */
export function insideOutsideKinds(
  group: ChartGroup,
  bars: { t: readonly number[]; h: readonly number[]; l: readonly number[] },
  settled: number,
): Map<number, BarKind> {
  const on = insideOutsideOf(group);
  const out = new Map<number, BarKind>();
  if (!on) return out;
  for (const b of classifyBars(bars, settled)) {
    if (b.kind === "inside" ? on.inside : on.outside) out.set(b.t, b.kind);
  }
  return out;
}
